
import { useState } from "react";
import { chatApi } from "../../services/chatApiService";
import { targetService } from "../../services/targetService";
import { useToast } from "@/hooks/use-toast";
import { ChatSession, Message, ProcessedFile } from "../types";
import { supabase } from "@/integrations/supabase/client";

// Import the internal message filtering function from ChatContext
// ENHANCED: More comprehensive filter for internal analysis requests and tool messages
const isInternalAnalysisMessage = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  const internalRequestPatterns = [
    // Original patterns for analysis requests
    /^Analyze remote viewing session for Target [A-Z0-9]{4}-[A-Z0-9]{4}\./,
    /^Target [A-Z0-9]{4}-[A-Z0-9]{4} revealed\./,
    // Additional patterns for internal prompts that may leak
    /^TARGET:\s*(Type:|https?:)/,
    /^USER_IMPRESSIONS:/,
    /^ANALYSIS_REQUEST:/,
    /^Please analyze the following remote viewing session/,
    /^Based on the target details and user impressions/,
    // Filter tool messages and internal processing
    /^\[TOOL_CALL\]/,
    /^\[INTERNAL_ANALYSIS\]/,
    /^SYSTEM_PROMPT:/,
    // Filter any message that contains internal analysis structure patterns
    /TARGET:.*TYPE:.*USER'S IMPRESSIONS:/s,
    /^\[INTERNAL_ANALYSIS\]\s*Analyze remote viewing session/,
    // Enhanced patterns to catch variations
    /^TARGET:.*USER'S IMPRESSIONS:/s,
    /^Analyze remote viewing session/,
    /CRITICAL INSTRUCTION: Use ONLY second person language/,
    /VISION_ANALYSIS_REQUEST/,
    /IMAGE_COUNT:/,
    /Examples of CORRECT language:/,
    /Examples of INCORRECT language/
  ];

  const isInternal = internalRequestPatterns.some(pattern => pattern.test(content));
  
  return isInternal;
};

export const useSessionManager = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentTargetCode, setCurrentTargetCode] = useState<string | null>(null);
  const [currentTargetId, setCurrentTargetId] = useState<string | null>(null);
  const [currentTargetType, setCurrentTargetType] = useState<'location' | 'image' | 'prediction' | null>(null);
  const { toast } = useToast();

  const createDefaultSession = async () => {
    try {
      console.log('🔄 SessionManager: Creating default session with welcome message...');
      
      // Clear target info for new default session
      setCurrentTargetCode(null);
      setCurrentTargetId(null);
      setCurrentTargetType(null);
      
      const newSession = await chatApi.createSession(`Welcome Session ${Date.now()}`);
      console.log('✅ SessionManager: Default session created:', newSession);
      
      // Add welcome message to the default session
      const welcomeMessage = `## Welcome to RemoteViz! 🌍

Your journey into remote viewing starts here:

🎯 **Generate a Target**
Click a button or type "Generate Location Target" for a random Google Street View location, or "Generate Image Target" for a random image. You'll get a unique 8-digit Target Code—your gateway to the unknown.

📓 **Submit Your Impressions:**
Share your sketches and written impressions. Your notes help evaluate accuracy.

🫣 **Reveal the Target:**
Click the "Reveal Target" button or type "Reveal Target" to uncover the hidden location or image.

💯 **Get Personalized Feedback:**
We'll compare your impressions to the actual target and help you improve your skills.

🙋 **Get Support Anytime:**
Just type "help" anytime for assistance.`;

      // Store welcome message directly in database as system message
      try {
        const { data, error } = await supabase
          .from('messages')
          .insert({
            session_id: newSession.id,
            user_id: (await supabase.auth.getUser()).data.user?.id,
            content: welcomeMessage,
            role: 'assistant'
          });

        if (error) throw error;
        console.log('✅ SessionManager: Welcome message stored directly in database');
      } catch (error) {
        console.error('❌ SessionManager: Failed to store welcome message:', error);
      }

      // Store welcome message in session for immediate display
      newSession.welcomeMessage = {
        id: `welcome-${newSession.id}`,
        content: welcomeMessage,
        type: 'system' as const,
        timestamp: Date.now()
      };
      
      // Add the new session to the beginning of the sessions list
      setSessions(prev => [newSession, ...prev]);
      
      // Set this as the current session
      console.log('🔄 SessionManager: Setting current session ID from default session:', newSession.id);
      setCurrentSessionId(newSession.id);
      
      console.log('✅ SessionManager: Default session set as current:', newSession.id);
      
      return newSession;
    } catch (error) {
      console.error('❌ SessionManager: Failed to create default session:', error);
      toast({
        title: "Error",
        description: "Failed to create default session",
        variant: "destructive",
      });
      throw error;
    }
  };

  const loadSessions = async () => {
    try {
      console.log('🔄 SessionManager: Loading sessions...');
      const sessionsData = await chatApi.getSessions();
      setSessions(sessionsData);
      
      // ALWAYS create a fresh default session on app load for proper onboarding
      console.log('📝 SessionManager: Creating fresh default session for onboarding...');
      await createDefaultSession();
      
    } catch (error) {
      console.error('❌ SessionManager: Failed to load sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load chat sessions",
        variant: "destructive",
      });
    }
  };

  const createNewSession = async () => {
    try {
      console.log('🔄 SessionManager: Creating new session...');
      
      // Clear target info immediately when creating new session
      setCurrentTargetCode(null);
      setCurrentTargetId(null);
      setCurrentTargetType(null);
      
      const newSession = await chatApi.createSession('New Chat');
      console.log('✅ SessionManager: New session created:', newSession);
      
      // Add welcome message for new chat session
      const newChatMessage = `🎉 **New Chat Started**

Ready to explore! Generate a target to begin your remote viewing journey.`;

      // Store new chat message directly in database as system message
      try {
        const { data, error } = await supabase
          .from('messages')
          .insert({
            session_id: newSession.id,
            user_id: (await supabase.auth.getUser()).data.user?.id,
            content: newChatMessage,
            role: 'assistant'
          });

        if (error) throw error;
        console.log('✅ SessionManager: New chat message stored directly in database');
      } catch (error) {
        console.error('❌ SessionManager: Failed to store new chat message:', error);
      }
      
      // Store new chat message in session for immediate display
      newSession.welcomeMessage = {
        id: `new-chat-${newSession.id}`,
        content: newChatMessage,
        type: 'system' as const,
        timestamp: Date.now()
      };
      // Add the new session to the beginning of the sessions list
      setSessions(prev => [newSession, ...prev]);
      
      // Set this as the current session
      console.log('🔄 SessionManager: Setting current session ID from new session:', newSession.id);
      setCurrentSessionId(newSession.id);
      
      console.log('✅ SessionManager: New session set as current, target code cleared:', newSession.id);
      
      toast({
        title: "Ready to explore!",
        description: "Your new session is ready. Generate a target to begin your remote viewing journey.",
      });
      
      return newSession;
    } catch (error) {
      console.error('❌ SessionManager: Failed to create session:', error);
      toast({
        title: "Error",
        description: "Failed to create new session",
        variant: "destructive",
      });
      throw error;
    }
  };

  const switchSession = async (sessionId: string) => {
    try {
      console.log('🔄 SessionManager: Setting current session ID from switchSession:', sessionId);
      setCurrentSessionId(sessionId);
      setCurrentTargetCode(null);
      setCurrentTargetId(null);
      setCurrentTargetType(null);
      const messagesData = await chatApi.getMessages(sessionId);
      
      // PHASE 4: Get target reveal status for this session
      const target = await targetService.getTargetBySession(sessionId);
      const isRevealed = target && !!target.revealed_at;
      console.log('🔄 SessionManager: Session target reveal status:', {
        hasTarget: !!target,
        targetCode: target?.target_code,
        isRevealed,
        revealedAt: target?.revealed_at
      });
      
      // CRITICAL FIX: Filter out internal analysis messages that shouldn't be shown to users
      const filteredMessages = messagesData.filter((msg: any) => {
        const content = msg.content || '';
        const isInternal = isInternalAnalysisMessage(content);
        if (isInternal) {
          console.log('🚫 SessionManager: BLOCKED internal analysis message from old session:', content.substring(0, 150));
        }
        return !isInternal;
      });
      
      const formattedMessages: Message[] = filteredMessages.map((msg: any) => {
        // Process file URLs into ProcessedFile objects for proper display
        let processedFiles: ProcessedFile[] = [];
        if (msg.file_urls && msg.file_urls.length > 0) {
          console.log('📦 SessionManager: Processing file URLs from old session:', msg.file_urls);
          processedFiles = msg.file_urls
            .filter(url => url && (url.startsWith('http') || url.startsWith('https')))
            .map((url: string, index: number) => ({
              type: 'image' as const,
              content: url,
              filename: url.split('/').pop() || `image-${index + 1}.jpg`
            }));
        }
        
        const formattedMessage: Message = {
          id: msg.id,
          content: msg.content,
          type: (msg.role === 'user' ? 'user' : 'system') as "system" | "user",
          timestamp: new Date(msg.created_at).getTime(),
          files: processedFiles.length > 0 ? processedFiles : undefined
        };
        
        return formattedMessage;
      });
      
      // Load the most recent target for this session
      if (target) {
        // Determine target type - prediction targets don't have target_type field
        const isPredictionTarget = 'event_id' in target;
        const targetType = isPredictionTarget ? 'prediction' : (target as any).target_type;
        console.log('🎯 SessionManager: Loaded existing target for session:', target.target_code, target.id, targetType);
        setCurrentTargetCode(target.target_code);
        setCurrentTargetId(target.id);
        setCurrentTargetType(targetType as 'location' | 'image' | 'prediction');
      }

      return formattedMessages;
    } catch (error) {
      console.error('❌ SessionManager: Failed to switch session:', error);
      toast({
        title: "Error",
        description: "Failed to switch session",
        variant: "destructive",
      });
      return [];
    }
  };

  const clearSession = () => {
    setCurrentSessionId(null);
    setCurrentTargetCode(null);
    setCurrentTargetId(null);
    setCurrentTargetType(null);
  };

  return {
    sessions,
    currentSessionId,
    currentTargetCode,
    currentTargetId,
    currentTargetType,
    setSessions,
    setCurrentSessionId,
    setCurrentTargetCode,
    setCurrentTargetId,
    setCurrentTargetType,
    loadSessions,
    createNewSession,
    switchSession,
    clearSession
  };
};
